"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopNavConsumer({
  scrolled
}: {
  scrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
      </div>;
  }
  if (scrolled) {
    return <nav className="flex items-center gap-8">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-foreground hover:text-primary font-mono text-sm uppercase tracking-widest transition-colors">
            {item.label}
          </Link>)}
      </nav>;
  }
  return <nav className="flex items-center gap-1 rounded-lg border border-white/20 bg-white/10 px-2 py-2 backdrop-blur-md">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-foreground hover:bg-white/15 font-mono text-sm uppercase tracking-widest px-4 py-2 rounded-md transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
function MobileNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-12 w-2/3 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-12 w-1/2 bg-muted/40 animate-pulse rounded-lg" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6 mt-8">
      {menuItems.map((item, i) => <SheetClose key={item.id} asChild>
          <Link data-index={i} to={item.href || "#"} className="font-default text-4xl text-foreground hover:text-primary transition-colors py-3 border-b border-border/30">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="719" id="main-navigation" className={`bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-background/75 backdrop-blur-lg border-b border-border/40" : "bg-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          <div className="flex-shrink-0">
            <Link to="/" aria-label="InfinitySecure home">
              <WvcLogo className="h-8 w-auto" />
            </Link>
          </div>

          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopNavConsumer scrolled={scrolled} />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex flex-shrink-0">
            <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default uppercase tracking-widest text-sm shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-shadow">
              <Link to="/ContactPage">
                Get Assessment
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <Button asChild size="sm" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default uppercase tracking-wider text-xs shadow-md shadow-primary/30">
              <Link to="/ContactPage">Contact</Link>
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-primary hover:bg-white/10 rounded-lg">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background/95 backdrop-blur-xl text-foreground border-l border-border/40 w-full sm:max-w-md p-0">
                <SheetHeader className="px-6 pt-6 pb-2 flex flex-row items-center justify-between space-y-0">
                  <SheetTitle className="font-default">
                    <WvcLogo className="h-7 w-auto" />
                  </SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close menu" className="text-foreground hover:bg-white/10 rounded-lg">
                      <X className="h-6 w-6" />
                    </Button>
                  </SheetClose>
                </SheetHeader>

                <MenuProvider menu_id="16">
                  <MobileNavConsumer />
                </MenuProvider>

                <div className="px-6 mt-10">
                  <SheetClose asChild>
                    <Button asChild size="lg" className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default uppercase tracking-widest text-sm shadow-lg shadow-primary/30">
                      <Link to="/ContactPage">
                        Get Assessment
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </SheetClose>
                </div>

                <div className="px-6 mt-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Cannington, Western Australia
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}